import { Component as ICalComponent, Event as ICalEvent, helpers as ICalHelpers, parse as ICalParse, stringify as ICalStringify, Time as ICalTime, Timezone as ICalTimezone, Recur, RecurExpansion } from 'ical.js';
import { Frequency, Options, RRule, Weekday } from 'rrule';
import { AlertDefaults, AlertSettings, ScheduledAlert, ScheduleSettings, ScheduleType } from 'src/app/hickory-api';
import { fixDate, formatDateTime } from 'src/app/shared/pipes/fix-date/fix-date.pipe';
import { Guid } from 'src/utility/guid';
import { tzlib_get_ical_block } from 'timezones-ical-library';
import { AlertModelService } from '../alert-model.service';
import { ALL_WEEKDAYS } from 'rrule/dist/esm/weekday';
import { toInteger } from 'src/app/shared/model/utility';
import { RecurOption } from '../../alert-schedule/custom-schedule/custom-schedule.component';
import { Observable, map, of } from 'rxjs';
import ENGLISH from 'rrule/dist/esm/nlp/i18n';
import { formatDate } from '@angular/common';
import { LocaleHelper } from 'src/app/l10n-config';

export type OccurrenceDetails = ICalEvent.occurrenceDetails;

export interface RecurComputed {
  startDate: Date;
  allComputed: Date[];
  description: string;
  timedOut: boolean;
}

export class ICalWrapper extends ICalComponent {
  static readonly UTC_ID: string = 'Etc/UTC';
  static readonly PRODID: string = '-//Konexus//Konexus Events//EN';
  static createEmpty(): ICalWrapper { return new ICalWrapper(); }
  static fromString(_: string): ICalComponent { throw new Error('use \'parseData()\' instead of \'fromString()\''); }
  static parseData(data: string, startDate: Date): ICalWrapper {
    if (!data) return null;
    try {
      const parsed = new ICalWrapper(ICalParse(data));
      if (startDate) parsed.setEventStart(startDate);
      return parsed;
    } catch (_err) {
      return null;
    }
  }

  constructor(parsed?: any[]) {
    if (parsed) {
      super(parsed, null);
    } else {
      super('vcalendar', null);
      this.addPropertyWithValue('prodid', ICalWrapper.PRODID);
      this.addPropertyWithValue('version', '2.0');
    }
    this.setTimezone();
  }

  get event(): ICalEvent {
    const vevent = this.getFirstSubcomponent('vevent');
    if (!vevent) return null;
    return new ICalEvent(vevent, null);
  }

  get eventStart(): ICalTime {
    return this.event?.startDate;
  }

  get eventStartDate(): Date {
    return this.eventStart?.toJSDate();
  }

  get localTzid(): string {
    return Intl.DateTimeFormat().resolvedOptions().timeZone;
  }

  get isUtc(): boolean {
    return this.localTzid === ICalWrapper.UTC_ID;
  }

  get recur(): Recur {
    return this.event?.component?.getFirstPropertyValue('rrule') as unknown as Recur;
  }

  get rrule(): RRule {
    const recur = this.recur;
    if (!recur?.parts) return null;
    const parts = this.recur.parts;
    const start = RecurHelper.dateToRrule(this.eventStartDate);
    const until = RecurHelper.dateToRrule(this.recur.until?.toJSDate());
    const options: Partial<Options> = {
      freq: RecurHelper.freqToRrule(recur.freq),
      dtstart: start,
      tzid: this.localTzid,
      until: until,
      interval: this.recur.interval,
      byweekday: RecurHelper.weeklyByDayToRrule(parts.BYDAY),
      bymonthday: RecurHelper.monthlyByDayToRrule(parts.BYMONTHDAY),
    }
    return new RRule(options);
  }

  setTimezone(tzid?: string) {
    tzid = tzid ?? this.localTzid;
    const tzdata = tzlib_get_ical_block(tzid); // generates the vtimezone block for us
    if (tzdata == null) return;

    const existing = this.getTimezone(tzid);
    if (existing != null) return;
    
    const tzcomp = ICalComponent.fromString(tzdata[0]);
    this.addSubcomponent(tzcomp);
  }

  getTimezone(tzid?: string): ICalTimezone {
    tzid = tzid ?? this.localTzid;
    return this.getAllTimeZones().find(t => t?.tzid === tzid);
  }

  getAllTimeZones(): Array<ICalTimezone> {
    const list = new Array<ICalTimezone>();
    const components = this.getAllSubcomponents('vtimezone');
    components.forEach(c => list.push(ICalTimezone.fromData(c)));
    return list;
  }

  createEvent(start?: Date, recur?: Recur): this {
    this.removeAllSubcomponents('vevent'); // only support a single event
    const comp = this.addSubcomponent(new ICalComponent('vevent', this));
    const event = new ICalEvent(comp, null);
    event.uid = Guid.newGuid();
    if (start) this.setEventStart(start);
    if (recur) this.setRecurrence(recur);
    return this;
  }

  setEventStart(start: Date) {
    if (!start) return;
    const event = this.event;
    if (!event) return
    start = fixDate(start);
    // don't use UTC here, since we are using a reference timezone
    const time = ICalTime.fromJSDate(start, this.isUtc);
    const zone = this.getTimezone();
    if (zone) time.zone = zone;
    event._setTime('dtstart', time);
    // also set the dtstamp
    event._setTime('dtstamp', time);
  }

  setRecurrence(options: Recur, until?: Date, updateStartDate: boolean = true): this {
    this.event?.component?.removeAllProperties('rrule'); // only support a single rrule
    if (!options) return this;
    if (until) {
      // set until time to 11:59pm on the end date
      const copy = new Date(until.getTime());
      copy.setHours(23);
      copy.setMinutes(59);
      options.until = ICalTime.fromJSDate(copy, true);
    }
    this.event?.component?.addPropertyWithValue('rrule', options);
    if (updateStartDate) {
      this.setEventStart(this.getFirstOccurrence());
    }

    return this;
  }

  getRecurrenceIterator(start?: Date): RecurExpansion {
    const time: ICalTime = start ? ICalTime.fromJSDate(start, true) : this.event?.startDate;
    return this.event?.iterator(time);
  }

  calculateRecurrenceInfo(from?: Date, max?: number): Observable<RecurComputed> {
    if (max == null) max = Number.MAX_SAFE_INTEGER;
    const rrule = this.rrule;
    const iterator = this.getRecurrenceIterator(from);
    if (!rrule || !iterator) return of(null);
    const list: Date[] = [];
    const chunkSize = 100;
    const chunkMaxElapsed = 500;
    const info: RecurComputed = {
      startDate: from ?? this.eventStartDate,
      allComputed: list,
      description: rrule.toText(),
      timedOut: false,
    };

    // Note: use with caution -- this can get expensive to calculate once we hit year 2034,
    // so we wrap it in this fake background task stuff so that it doesn't lock the UI
    return new Observable<RecurComputed>((subscriber) => {
      const processOccurrences = function() {
        let chunkIndex = 0;
        const start = Date.now();
        while (!iterator.complete && chunkIndex < chunkSize && (list.length < max)) {
          chunkIndex++;
          const next = iterator.next();
          if (next) {
            list.push(next.toJSDate());
          }
        }

        if (subscriber.closed) return;
  
        if (!iterator.complete && list.length < max) {
          const elapsed = Date.now() - start;
          if (elapsed > chunkMaxElapsed) {
            info.timedOut = true;
            subscriber.next(info);
            subscriber.complete();
          } else {
            setTimeout(processOccurrences, 100);
          }
        } else {
          subscriber.next(info);
          subscriber.complete();
        }
      }

      processOccurrences();
    });
  }

  getRecurrenceHumanReadable(): string {
    const gettext = (id: string | number | Weekday): string => { return id.toString(); };
    const formatter = (year: number, month: string, day: number): string => {
      const m = ENGLISH.monthNames.indexOf(month);
      if (m >= 0) {
        const date = new Date(Date.UTC(year, m, day));
        return formatDate(date, 'MMMM dd, yyyy', LocaleHelper.defaultLocale);
      } else {
        return `${month} ${day}, ${year}`;
      }
    }
    return this.rrule?.toText(gettext, ENGLISH, formatter);
  }

  getFirstOccurrence(from?: Date): Date {
    return this.getRecurrenceIterator(from)?.next()?.toJSDate();
  }

  getNextOccurrences(from?: Date, count?: number): Observable<Array<Date>> {
    return this.calculateRecurrenceInfo(from, count).pipe(map(r => r?.allComputed ?? []));
  }

  stringify(finalize: boolean = true): string {
    if (finalize) this.finalize();
    return ICalStringify(this.toJSON() as any[]);
  }

  finalize(): this {
    ICalHelpers.updateTimezones(this);
    return this;
  }
}

export class RecurHelper {
  static readonly HOURLY = 'HOURLY';
  static readonly DAILY = 'DAILY';
  static readonly WEEKLY = 'WEEKLY';
  static readonly MONTHLY = 'MONTHLY';
  static readonly YEARLY = 'YEARLY';

  static readonly SU = 'SU';
  static readonly MO = 'MO';
  static readonly TU = 'TU';
  static readonly WE = 'WE';
  static readonly TH = 'TH';
  static readonly FR = 'FR';
  static readonly SA = 'SA';

  static readonly ALL_WEEK_DAYS = [ RecurHelper.SU, RecurHelper.MO, RecurHelper.TU, RecurHelper.WE, RecurHelper.TH, RecurHelper.FR, RecurHelper.SA ];
  static readonly WEEKDAY_DAYS = [ RecurHelper.MO, RecurHelper.TU, RecurHelper.WE, RecurHelper.TH, RecurHelper.FR ];
  static readonly WEEKEND_DAYS = [ RecurHelper.SA, RecurHelper.SU ];

  static readonly RECUR_DAILY: Recur = new Recur({ freq: RecurHelper.DAILY, });
  static readonly RECUR_WEEKDAYS: Recur = new Recur({ freq: RecurHelper.WEEKLY, byday: RecurHelper.WEEKDAY_DAYS, });
  static readonly RECUR_WEEKENDS: Recur = new Recur({ freq: RecurHelper.WEEKLY, byday: RecurHelper.WEEKEND_DAYS, });
  static readonly RECUR_WEEKLY: Recur = new Recur({ freq: RecurHelper.WEEKLY, });
  static readonly RECUR_BIWEEKLY: Recur = new Recur({ freq: RecurHelper.WEEKLY, interval: 2, });
  static readonly RECUR_MONTHLY: Recur = new Recur({ freq: RecurHelper.MONTHLY, });
  static readonly RECUR_BIMONTHLY: Recur = new Recur({ freq: RecurHelper.MONTHLY, interval: 2, });
  static readonly RECUR_QUARTERLY: Recur = new Recur( { freq: RecurHelper.MONTHLY, interval: 3, });
  static readonly RECUR_SEMIANNUALLY: Recur = new Recur({ freq: RecurHelper.MONTHLY, interval: 6, });
  static readonly RECUR_YEARLY: Recur = new Recur({ freq: RecurHelper.YEARLY, });

  static weekdayToRrule(input: string): Weekday {
    if (!input || input?.length < 2) return null;
    const suffix = input.substring(input.length - 2, input.length);
    const day = (ALL_WEEKDAYS.includes(suffix as any)) ? Weekday.fromStr(suffix as any) : null;
    if (!day || suffix === input) return day;
    const prefix = input.substring(0, input.length - 2);
    const n = toInteger(prefix)
    return day.nth(n);
  }

  static weeklyByDayToRrule(byday: string[]): Weekday[] {
    if (!byday?.length) return null;
    return byday.map(d => this.weekdayToRrule(d)).filter(o => !!o);
  }

  static monthlyByDayToRrule(monthlyByday: number[]): number[] {
    return monthlyByday;
  }

  static freqToRrule(freq: string): Frequency { 
    switch (freq) {
      case RecurHelper.YEARLY: return Frequency.YEARLY;
      case RecurHelper.MONTHLY: return Frequency.MONTHLY;
      case RecurHelper.WEEKLY: return Frequency.WEEKLY;
      case RecurHelper.DAILY: return Frequency.DAILY;
    }

    return null
  }

  static dateToRrule(date: Date): Date {
    if (!date) return null;
    return new Date(Date.UTC(date.getUTCFullYear(), date.getUTCMonth(), date.getUTCDate(), date.getUTCHours(), date.getUTCMinutes()));
  }

  static fromString(rrule: string): Recur {
    return rrule ? Recur.fromData(Recur._stringToData(rrule, true)) : null;
  }

  static weekNumber(date: Date): number {
    return Math.ceil((date?.getDate() ?? 1) / 7);
  }

  static isLastWeek(date: Date): boolean {
    if (!date) return false;
    const lastDayOfMonth = new Date(date.getFullYear(), date.getMonth(), 0);
    return this.weekNumber(date) === this.weekNumber(lastDayOfMonth);
  }

  static isMonthlyByWeekday(recur: Recur): boolean {
    return recur?.freq === RecurHelper.MONTHLY && recur?.parts?.BYDAY?.[0]?.length > 2;
  }

  static isMontlyByDate(recur: Recur): boolean {
    return recur?.freq === RecurHelper.MONTHLY && !this.isMonthlyByWeekday(recur);
  }

  static isPossiblySkippingMonths(recur: Recur, start: Date): boolean {
    if (!start) return false;
    if (start.getDate() <= 28) return false;
    // eslint-disable-next-line sonarjs/prefer-single-boolean-return
    if (!this.isMontlyByDate(recur)) return false;
    return true;
  }

  static calculateMonthlyByMonthDay(start: Date): RecurOption {
    if (!start) return null;
    return { value: start.getDate(), display: `Monthly on day ${start.getDate()}` };
  }

  static calculateMonthlyByWeekDay(start: Date): RecurOption[] {
    const options: RecurOption[] = [];
    const day = this.ALL_WEEK_DAYS[start.getDay()]; // 'MO'
    const dayName = start.toLocaleDateString('en-US', { weekday: 'long' }); // 'Monday'
    const weekNo = RecurHelper.weekNumber(start) % 10; // 3
    let nth: string = `${weekNo}th`; // '3rd'
    switch (weekNo) {
      case 1: nth = `${weekNo}st`; break;
      case 2: nth = `${weekNo}nd`; break;
      case 3: nth = `${weekNo}rd`; break;
      case 4: nth = `${weekNo}th`; break;
    }
    options.push({ value:`${weekNo}${day}`, display: `Monthly on the ${nth} ${dayName}` });
    if (RecurHelper.isLastWeek(start)) {
      options.push({ value:`-1${day}`, display: `Monthly on the last ${dayName}` });
    }
    return options;
  }

  static jsDateToICalTime(date: Date, utc: boolean = false): ICalTime {
    return ICalTime.fromJSDate(date, utc);
  }
}

export class ScheduleOption {
  static readonly CUSTOM_RECUR = 999;

  static readonly RECUR_DAILY = RecurHelper.RECUR_DAILY.toString();
  static readonly RECUR_WEEKDAYS = RecurHelper.RECUR_WEEKDAYS.toString();
  static readonly RECUR_WEEKENDS = RecurHelper.RECUR_WEEKENDS.toString();
  static readonly RECUR_WEEKLY = RecurHelper.RECUR_WEEKLY.toString();
  static readonly RECUR_BIWEEKLY = RecurHelper.RECUR_BIWEEKLY.toString();
  static readonly RECUR_MONTHLY = RecurHelper.RECUR_MONTHLY.toString();

  static readonly amHourOptions: ScheduleOption[] = [
    new ScheduleOption(0),
    new ScheduleOption(1),
    new ScheduleOption(2),
    new ScheduleOption(3),
    new ScheduleOption(4),
    new ScheduleOption(5),
    new ScheduleOption(6),
    new ScheduleOption(7),
    new ScheduleOption(8),
    new ScheduleOption(9),
    new ScheduleOption(10),
    new ScheduleOption(11),
  ];

  static readonly pmHourOptions: ScheduleOption[] = [
    new ScheduleOption(12),
    new ScheduleOption(13),
    new ScheduleOption(14),
    new ScheduleOption(15),
    new ScheduleOption(16),
    new ScheduleOption(17),
    new ScheduleOption(18),
    new ScheduleOption(19),
    new ScheduleOption(20),
    new ScheduleOption(21),
    new ScheduleOption(22),
    new ScheduleOption(23),
  ];

  static readonly minuteOptions: ScheduleOption[] = [
    new ScheduleOption(0, '00'),
    new ScheduleOption(5, '05'),
    new ScheduleOption(10),
    new ScheduleOption(15),
    new ScheduleOption(20),
    new ScheduleOption(25),
    new ScheduleOption(30),
    new ScheduleOption(35),
    new ScheduleOption(40),
    new ScheduleOption(45),
    new ScheduleOption(50),
    new ScheduleOption(55),
  ];

  static readonly defaultRecurring: ScheduleOption = new ScheduleOption(0, 'Not Recurring');
  static readonly recurringOptions: ScheduleOption[] = [
    this.defaultRecurring,
    new ScheduleOption(1, 'Daily', ScheduleOption.RECUR_DAILY),
    new ScheduleOption(2, 'Weekdays (Mon-Fri)', ScheduleOption.RECUR_WEEKDAYS),
    new ScheduleOption(3, 'Weekends (Sat-Sun)', ScheduleOption.RECUR_WEEKENDS),
    new ScheduleOption(7, 'Weekly', ScheduleOption.RECUR_WEEKLY),
    new ScheduleOption(14, 'Bi-Weekly', ScheduleOption.RECUR_BIWEEKLY),
    new ScheduleOption(30, 'Monthly', ScheduleOption.RECUR_MONTHLY),
    new ScheduleOption(ScheduleOption.CUSTOM_RECUR, 'Custom…'),
  ];

  static icalToLegacy(recurData: string): number {
    if (!recurData) return 0;
    const expression = recurData?.split(';UNTIL')?.[0];
    switch (expression) {
      case ScheduleOption.RECUR_DAILY:   return 1;
      case ScheduleOption.RECUR_WEEKDAYS: return 2;
      case ScheduleOption.RECUR_WEEKENDS: return 3;
      case ScheduleOption.RECUR_WEEKLY: return 7;
      case ScheduleOption.RECUR_BIWEEKLY: return 14;
      case ScheduleOption.RECUR_MONTHLY: return 30;
      default: {
        return ScheduleOption.CUSTOM_RECUR;
      }
    }
  }

  static legacyToIcal(recurrence: number): string {
    if (!recurrence) return null;
    switch (recurrence) {
      case  1: return ScheduleOption.RECUR_DAILY;      // daily
      case  2: return ScheduleOption.RECUR_WEEKDAYS;   // weekdays
      case  3: return ScheduleOption.RECUR_WEEKENDS;   // weekends
      case  7: return ScheduleOption.RECUR_WEEKLY;     // weekly
      case 14: return ScheduleOption.RECUR_BIWEEKLY;   // bi-weekly
      case 30: return ScheduleOption.RECUR_MONTHLY;    // monthly
      default: return null;
    }
  }

  get viewValue(): string { return this._viewValue || this.value.toString(); }

  constructor(
    public value: number,
    private _viewValue?: string,
    public recurValue?: string,
  ) { }
}

export class AlertScheduleHelper {
  static postProcess(service: AlertModelService, alert: AlertDefaults = service.alertSettings) {
    const schedule = alert.schedule;
    if (!schedule) return;
    this.fixDates(service, schedule);
    if (service.useSchedulerV2) {
      schedule.type = ScheduleType.Balsa;
      schedule.recurringDay = undefined;
      schedule.icalData = this.fixIcalData(schedule.icalData, schedule.scheduledDateTime);
    } else {
      schedule.type = ScheduleType.Hickory;
      schedule.icalData = undefined;
    }
  }

  static preparePreview(service: AlertModelService, preview: AlertDefaults) {
    const schedule = preview.schedule;
    if (!schedule) return;
    if (schedule.icalData) {
      const parsed = ICalWrapper.parseData(schedule.icalData, schedule.scheduledDateTime);
      schedule.icalData = parsed?.finalize()?.stringify();
      schedule.recurringDay = schedule.recurringDay ?? ScheduleOption.icalToLegacy(parsed?.recur?.toString());
    }

    if (!service.isEditScheduled) {
      // ROAD-8616: clear out the scheduledAlertSettingsId if we're not editing a scheduled alert
      schedule.scheduledAlertSettingsId = undefined;
    }
  }

  static recurringOptionSummary(service: AlertModelService): string {
    return this.getRecurringName(service.alertSchedule?.recurringDay);
  }

  static startDateSummary(service: AlertModelService, language: string): string {
    const startDate = service.alertSchedule?.scheduledDateTime;
    if (!startDate) return null;
    return formatDateTime(startDate, language, true);
  }

  static getRecurringName(option: number): string {
    const selected = ScheduleOption.recurringOptions.find(o => option === o.value);
    return selected?.viewValue || ScheduleOption.defaultRecurring.viewValue;
  }

  static getRecurringDescription(service: AlertModelService, schedule: ScheduleSettings = service.alertSchedule): string {
    if (!schedule?.scheduled || !schedule?.scheduledDateTime || !schedule?.recurringEnd) return undefined;
    const fixedStart = fixDate(schedule.scheduledDateTime);
    const ical = ICalWrapper.parseData(schedule.icalData, fixedStart);
    return ical?.getRecurrenceHumanReadable() ?? this.recurringOptionSummary(service);
  }

  static validate(service: AlertModelService, schedule: ScheduleSettings = service.alertSchedule): string[] {
    const errors = [];
    if (!schedule?.scheduled) return errors;

    const now = new Date();
    if (!schedule.scheduledDateTime) return ['Alert.ScheduleAlert.StartDateMustBeSelected'];
    if (schedule.scheduledDateTime < now) errors.push('Alert.ScheduleAlert.StartDateMustBeAfterCurrentDate');

    if (schedule.recurringDay > 0 || !!schedule.icalData) {
      if(schedule.scheduledDateTime == null){
        errors.push('Alert.ScheduleAlert.StartDateMustBeSelected');
      } else if (schedule.recurringEnd == null) {
        errors.push('Alert.ScheduleAlert.RecurringEndDateNotSelected');
      } else if (schedule.scheduledDateTime > schedule.recurringEnd) {
        errors.push('Alert.ScheduleAlert.StartDateAterEndDate');
      }
    }

    return errors;
  }

  // like validate(), but for warning-level issues (not errors)
  static checkWarnings(schedule: ScheduleSettings, ical: ICalWrapper): string[] {
    const warnings = [];
    if (!schedule?.scheduled || !schedule?.scheduledDateTime) return warnings;
    const recur = ical?.recur;
    if (RecurHelper.isPossiblySkippingMonths(recur, schedule.scheduledDateTime)) {
      const dayOfMonth = schedule.scheduledDateTime?.getDate();
      warnings.push(`Warning: Not all months have ${dayOfMonth} days. Some months may be skipped.`);
    }

    return warnings;
  }

  static fixDates(service: AlertModelService, schedule: ScheduleSettings = service.alertSchedule) {
    // convert the schedule date value(s) (if present) from .NET style
    if (schedule) {
      schedule.expireDateTimeUtc = fixDate(schedule.expireDateTimeUtc) ?? undefined;
      schedule.recurringEnd = fixDate(schedule.recurringEnd) ?? undefined;
      schedule.scheduledDateTime = fixDate(schedule.scheduledDateTime) ?? undefined;
    }
  }

  static fixIcalData(icalData: string, startDate: Date, finalize: boolean = false): string {
    if (!icalData || !startDate) return icalData;
    const parsed = ICalWrapper.parseData(icalData, startDate);
    if (finalize) parsed?.finalize();
    return parsed?.stringify();
  }

  static toScheduledAlert(alert: AlertSettings): ScheduledAlert {
    if (alert.schedule?.scheduled !== true || !alert.schedule.scheduledDateTime) return null;
    const scheduled: ScheduledAlert = {
      ScheduleDate: alert.schedule?.scheduledDateTime,
      EndDate: alert.schedule?.recurringEnd,
      IntervalInDays: alert.schedule?.recurringDay,
    }

    return scheduled;
  }

  static resetScheduleSettings(service: AlertModelService): ScheduleSettings {
    const useV2 = service.useSchedulerV2;
    // note -- this should preserve _some_ properties (like duration, scheduledAlertSettingsId, etc)
    const schedule = service.alertDefaults?.schedule ?? { };
    schedule.recurringDay = undefined;
    schedule.recurringEnd = undefined;
    schedule.icalData = undefined;
    schedule.scheduled = false;
    schedule.scheduledDateTime = undefined;
    schedule.type = useV2 ? ScheduleType.Balsa : ScheduleType.Hickory;
    if (!service.isEditScheduled) {
      // ROAD-8616: clear out the scheduledAlertSettingsId if we're not editing a scheduled alert
      schedule.scheduledAlertSettingsId = undefined;
    }
    this.fixDates(null, schedule);
    return schedule;
  }

  static resetSeconds(date: Date): Date {
    date?.setSeconds(0);
    date?.setMilliseconds(0);
    return date;
  }
}
